import React from "react";

import { times } from "lodash/fp";
import { useSearchParams } from "react-router-dom";

import ProgressStep, {
  LoadingProgressStep,
} from "@app/analytics/onboarding/components/ProgressStep";
import { TextButton } from "@app/analytics/onboarding/components/styles";
import * as Events from "@app/analytics/tracking/events";
import { parseReportInviteFromUrl } from "@app/common/utils/parsingUtils";

import { Link } from "@rui/Link";
import ReadLogo from "@rui/ReadLogo";
import { Column, Row } from "@rui/Stack";

import {
  ProgressContainer,
  ProgressInnerContainer,
  ProgressTitle,
  ProgressDescription,
  ProgressStepContainer,
} from "./styles";
import { SignupSource, StepState } from "./types";

const PRODUCT_NAME_FOR_SIGNUP_SOURCE: { [key in SignupSource]: string } = {
  default: "better meetings",
  rss: "Read Smart Scheduler",
  meet: "Read Notetaker",
};

interface WelcomeProgressProps {
  firstName?: string;
  signupSource: SignupSource;
  steps: StepState[];
  activeStepIndex: number;
  loading: boolean;
}

const LoadingSteps = () => (
  <>
    {times(
      (i) => (
        <LoadingProgressStep key={i} />
      ),
      3
    )}
  </>
);

const WelcomeProgress = ({
  firstName,
  signupSource,
  steps,
  activeStepIndex,
  loading,
}: WelcomeProgressProps) => {
  const [queryParams] = useSearchParams();
  const redirectTo = queryParams.get("redirectTo");
  const reportRedirectTo = redirectTo ? decodeURIComponent(redirectTo) : null;
  const hasReportInvite = parseReportInviteFromUrl(reportRedirectTo) != null;

  const activeStep = steps[activeStepIndex];
  const handleSkipForNowClick = () => {
    Events.skipForNow();
  };

  return (
    <ProgressContainer>
      <ProgressInnerContainer>
        <ReadLogo />
        <Column spacing={1}>
          <ProgressTitle>
            Welcome to Read{firstName ? `, ${firstName}` : null}
          </ProgressTitle>
          <ProgressDescription>
            To get started with {PRODUCT_NAME_FOR_SIGNUP_SOURCE[signupSource]},
            complete your account setup in a few easy steps.
          </ProgressDescription>
        </Column>
        <ProgressStepContainer numSteps={steps.length}>
          <ProgressStep
            title="Create Account"
            status="complete"
            active={false}
          />
          {loading ? (
            <LoadingSteps />
          ) : (
            steps.map((step) => (
              <ProgressStep
                key={step.id}
                {...step}
                active={activeStep?.id === step.id}
              />
            ))
          )}
        </ProgressStepContainer>
      </ProgressInnerContainer>
      {hasReportInvite && reportRedirectTo && (
        <Row justifySelf="flex-end">
          <Link to={reportRedirectTo}>
            <TextButton onClick={handleSkipForNowClick}>
              Skip for now
            </TextButton>
          </Link>
        </Row>
      )}
    </ProgressContainer>
  );
};

export default WelcomeProgress;
